import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import { usePageSetting } from "tsi-common-react/src/utils/settings";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import {
    Context,
    type CustomerSupportCardsBlock as CustomerSupportCardsBlockValue,
} from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Template as ChatLinkTemplate } from "../../templates/ChatLinkTemplate";

import styles from "./index.module.scss";

type Card = NonNullable<CustomerSupportCardsBlockValue["cards"]>[number];

const getStaticURL = (staticURL: string, path: string): string =>
    `${staticURL}${path}`;

const useStaticURL = (): string => {
    const context = React.useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return context?.STATIC_URL ?? "/static/";
};

const richTextContents = (copy: string): string =>
    copy
        .replace(/^<div class="rich-text">/, "")
        .replace(/^<div class='rich-text'>/, "")
        .replace(/<\/div>$/, "");

const CardCopy = (
    props: Immutable<{ copy: string | null | undefined }>,
): React.JSX.Element | null => {
    if (!props.copy) {
        return null;
    }
    return (
        <div
            className={concatClassNames([styles.supportCardCopy, "rich-text"])}
            dangerouslySetInnerHTML={{ __html: richTextContents(props.copy) }}
        />
    );
};

const ChatCard = (
    props: Immutable<{
        card: Extract<Card, { type: "chat_card" }>;
        iconURL: string;
    }>,
): React.JSX.Element => {
    const contactUsForm = usePageURL("contact_us_form");
    const chatOfflineLink = contactUsForm
        ? isoWebPageURL.unwrap(contactUsForm)
        : null;
    return (
        <>
            <div className={styles.supportCard}>
                <img
                    src={props.iconURL}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <h3>{t`Live Chat`}</h3>
                <CardCopy copy={props.card.value.copy} />
            </div>
            <ChatLinkTemplate
                class_name={concatClassNames([
                    styles.supportCardButton,
                    "button",
                    "button--secondary",
                ])}
                chat_online_text="Chat Now"
                chat_offline_text=""
                chat_offline_link={chatOfflineLink}
                hide_if_offline={true}
            />
        </>
    );
};

const PhoneCard = (
    props: Immutable<{
        card: Extract<Card, { type: "phone_card" }>;
        iconURL: string;
    }>,
): React.JSX.Element => {
    const phoneNumberLink = usePageSetting("phone_number_link");
    return (
        <>
            <div className={styles.supportCard}>
                <img
                    src={props.iconURL}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <h3>{t`Phone`}</h3>
                <CardCopy copy={props.card.value.copy} />
            </div>
            <a
                href={phoneNumberLink ?? ""}
                className={concatClassNames([
                    styles.supportCardButton,
                    "button",
                    "button--secondary",
                ])}
            >
                {t`Call Now`}
            </a>
        </>
    );
};

const EmailCard = (
    props: Immutable<{
        card: Extract<Card, { type: "email_card" }>;
        iconURL: string;
    }>,
): React.JSX.Element => {
    return (
        <>
            <div className={styles.supportCard}>
                <img
                    src={props.iconURL}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <h3>{t`Email`}</h3>
                <CardCopy copy={props.card.value.copy} />
            </div>
            <a
                href={props.card.value.cta_link?.url ?? ""}
                className={concatClassNames([
                    styles.supportCardButton,
                    "button",
                    "button--secondary",
                ])}
            >
                {t`Email Now`}
            </a>
        </>
    );
};

const SupportCard = (
    props: Immutable<{ card: Card; staticURL: string }>,
): React.JSX.Element | null => {
    if (props.card.type === "chat_card") {
        return (
            <ChatCard
                card={props.card}
                iconURL={getStaticURL(
                    props.staticURL,
                    "img/customer-support-cards/chat.svg",
                )}
            />
        );
    }
    if (props.card.type === "phone_card") {
        return (
            <PhoneCard
                card={props.card}
                iconURL={getStaticURL(
                    props.staticURL,
                    "img/customer-support-cards/phone.svg",
                )}
            />
        );
    }
    if (props.card.type === "email_card") {
        return (
            <EmailCard
                card={props.card}
                iconURL={getStaticURL(
                    props.staticURL,
                    "img/customer-support-cards/email.svg",
                )}
            />
        );
    }
    return null;
};

export const CustomerSupportCardsBlock = (
    props: Immutable<{ value: CustomerSupportCardsBlockValue }>,
): React.JSX.Element => {
    const staticURL = useStaticURL();
    const value = props.value;
    return (
        <section
            className={concatClassNames([
                styles.customerSupportCardsBlock,
                getGlobalSpacingClasses(value.spacing),
            ])}
        >
            {(value.cards ?? []).map((card) => (
                <div key={card.id}>
                    <SupportCard card={card} staticURL={staticURL} />
                </div>
            ))}
        </section>
    );
};
